import React, { useState, useMemo, useCallback } from "react";
import { useLanguage } from "./LanguageContext";

/**
 * CategoryFilter-Komponente
 * -------------------------
 * Dropdown-Filter für die Auswahl von Kategorien (mehrsprachig).
 *
 * Props:
 * - data: Array mit Stand-Daten (z. B. data.stands)
 * - selected: Array der aktuell ausgewählten Kategorien (Strings)
 * - onChange: Callback bei Änderung der Auswahl
 * - multiSelect: erlaubt Mehrfachauswahl (default: true)
 */
export default function CategoryFilter({
  // data = [],
  selected = [],
  onChange = () => {},
  multiSelect = true,
}) {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);

  /* ===================== HELPER: Übersetzung ===================== */
  const t = useCallback(
    (field, fallback = "de") => {
      if (!field) return "";
      if (typeof field === "string") return field;
      return field[lang] ?? field[fallback] ?? "";
    },
    [lang]
  );

  /* ===================== KATEGORIEN-LISTE (DE & EN) ===================== */
  const CATEGORIES = useMemo(() => [
    { de: "Antipasti", en: "Antipasti" },
    { de: "Backwaren & Snacks", en: "Bakery Products & Snacks" },
    { de: "Fertiggerichte", en: "Ready Meals" },
    { de: "Fleisch", en: "Meat" },
    { de: "Gemüse & Obst", en: "Vegetables & Fruit" },
    { de: "Getränke", en: "Beverages" },
    { de: "Glutenfreie Produkte", en: "Gluten-free Products" },
    { de: "Honig & Süßes", en: "Honey & Sweets" },
    { de: "Käse", en: "Cheese" },
    { de: "Kaffee", en: "Coffee" },
    { de: "Konserven", en: "Preserves" },
    { de: "Kosher", en: "Kosher" },
    { de: "ITA DESK", en: "ITA DESK" },
    { de: "Mehle & Getreide", en: "Flours & Grains" },
    { de: "Oliven & Olivenöl", en: "Olives & Olive Oil" },
    { de: "Pasta & Teigwaren", en: "Pasta" },
    { de: "Reis & Hülsenfrüchte", en: "Rice & Legumes" },
    { de: "Samen", en: "Seeds" },
    { de: "Tomatenprodukte", en: "Tomato Products" },
    { de: "Trockenfrüchte & Nüsse", en: "Dried Fruit & Nuts" },
    { de: "Vegane Produkte", en: "Vegan Products" },
    { de: "Wein & alkoholische Getränke", en: "Wine & Alcoholic Beverages" },
    { de: "Würzmittel & Kräuter", en: "Spices & Herbs" },
    { de: "Obst & Gemüse", en: "Fruit & Vegetables" },
  ], []);

  /* ===================== SORTIERUNG ===================== */
  const categoryStrings = useMemo(() => {
    return CATEGORIES
      .map((c) => t(c)) // Übersetze jede Kategorie
      .sort((a, b) => a.localeCompare(b, lang, { sensitivity: "base" }));
  }, [CATEGORIES, t, lang]);

  /* ===================== INTERAKTION ===================== */
  const toggleCategory = (cat) => {
    if (!multiSelect) {
      onChange([cat]); // Einfachauswahl
    } else if (selected.includes(cat)) {
      onChange(selected.filter((c) => c !== cat)); // Abwählen
    } else {
      onChange([...selected, cat]); // Hinzufügen
    }
  };

  const resetCategories = () => onChange([]); // Alles zurücksetzen
  const hasSelection = selected.length > 0;

  /* ===================== RENDER ===================== */
  return (
    <div className="relative w-80">
      {/* Dropdown-Button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-2 border rounded flex justify-between items-center bg-white"
      >
        {selected.length > 0
          ? selected.join(", ")
          : lang === "de"
          ? "Kategorie auswählen"
          : "Select categories"}
        <span>{open ? "▲" : "▼"}</span>
      </button>

      {/* Dropdown-Inhalt */}
      {open && (
        <div className="absolute z-50 w-full mt-1 border rounded bg-white max-h-64 overflow-auto shadow-lg">
          
          {/* 🔹 Sticky "Alles zurücksetzen"-Leiste */}
          <div
            onClick={hasSelection ? resetCategories : undefined}
            className={`
              sticky top-0 z-10
              p-3 border-b text-sm font-medium
              bg-white
              ${hasSelection
                ? "text-red-600 cursor-pointer hover:bg-red-50"
                : "text-green-600 cursor-default"}
            `}
          >
            {hasSelection
              ? (lang === "de" ? "Alles zurücksetzen" : "Reset all")
              : (lang === "de" ? "Alle Kategorien aktiv" : "All categories active")}
          </div>

          {/* Liste der Kategorien */}
          {categoryStrings.map((cat) => (
            <label
              key={cat}
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(cat)}
                onChange={() => toggleCategory(cat)}
                className="mr-2"
              />
              {cat}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
